import {
  CustomerResponse,
  DonationMetadata,
  InvoiceResponse,
  PaymentIntentResponse,
  PriceResponse,
  SetupIntentResponse,
  StripeInvoiceExpanded,
  StripeInvoiceLite,
  SubscriptionResponse,
} from './stripe-models';
import {
  LatestInvoiceSlim,
  StoredSubscription,
  StripeLatestInvoiceExpanded,
} from './stripe-subscription';

/** Elimina claves con null/undefined para no llenar el store de valores vacíos */
function compact<T extends Record<string, any>>(obj: T): T {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== null && v !== undefined) out[k] = v;
  }
  return out as T;
}

const asId = (x: unknown): string | null =>
  typeof x === 'string'
    ? x
    : x && typeof x === 'object' && 'id' in (x as any)
    ? (x as any).id
    : null;

/** Customer -> estado (sin object/description/created) */
export function mapCustomerToStore(
  r: CustomerResponse
): Omit<CustomerResponse, 'object' | 'description' | 'created'> {
  return compact({
    id: r.id,
    name: r.name,
    email: r.email,
    address: r.address ?? undefined,
    phone: r.phone ?? undefined,
  });
}

/** Price -> estado (sin object/active/unit_amount_decimal) */
export function mapPriceToStore(
  r: PriceResponse
): Omit<PriceResponse, 'object' | 'active' | 'unit_amount_decimal'> {
  return compact({
    id: r.id,
    currency: r.currency ?? undefined,
    unit_amount: r.unit_amount ?? null,
    product: r.product ?? undefined,
    type: r.type ?? undefined,
    recurring: r.recurring ?? null,
    nickname: r.nickname ?? null,
    metadada: r.metadata,
  });
}

/** compact<T> elimina claves con undefined/null si así lo tienes implementado */
export const mapInvoiceToStore = (i: StripeInvoiceLite): InvoiceResponse => {
  const customerId =
    typeof i.customer === 'string' ? i.customer : i.customer?.id ?? '';

  const paymentIntentId = i.payment_intent
    ? typeof i.payment_intent === 'string'
      ? i.payment_intent
      : i.payment_intent?.id
    : undefined;

  return compact<InvoiceResponse>({
    id: i.id,
    customerId,
    paymentIntentId,

    amountDue: i.amount_due ?? 0,
    amountPaid: i.amount_paid ?? 0,
    currency: i.currency,

    // En el objeto de Stripe status puede ser null mientras está en ciertos estados;
    // tu tipo lo requiere, así que por seguridad caemos a 'draft'.
    status: (i.status ?? 'draft') as InvoiceResponse['status'],
    collectionMethod: i.collection_method,

    hostedInvoiceUrl: i.hosted_invoice_url ?? undefined,
    pdfUrl: i.invoice_pdf ?? undefined,

    description: i.description ?? undefined,
    footer: i.footer ?? undefined,
    metadata: (i.metadata ?? undefined) as unknown as DonationMetadata,

    created: i.created,
  });
};

/** PaymentIntent -> estado (sin object/customer/metadata) */
export function mapPaymentIntentToStore(
  r: PaymentIntentResponse
): Omit<PaymentIntentResponse, 'object' | 'customer' | 'metadata'> {
  return compact({
    id: r.id,
    amount: r.amount,
    currency: r.currency,
    status: r.status ?? undefined,
    client_secret: r.client_secret ?? undefined,
  });
}

/** SetupIntent -> estado (sin object/customer) */
export function mapSetupIntentToStore(
  r: SetupIntentResponse
): Omit<SetupIntentResponse, 'object' | 'customer'> {
  return compact({
    id: r.id,
    status: r.status ?? undefined,
    payment_method: r.payment_method ?? undefined,
    client_secret: r.client_secret ?? undefined,
  });
}

// ——— Mapper: Stripe -> LatestInvoiceSlim ————————————————————
export function slimLatestInvoice(
  inv: StripeLatestInvoiceExpanded
): LatestInvoiceSlim {
  console.log('slimLatestInvoice passing here', inv);
  const lines = inv.lines?.data ?? [];

  return compact<LatestInvoiceSlim>({
    id: inv.id,
    slimResponse: true,
    status: inv.status ?? null,
    currency: inv.currency,
    total: inv.total ?? 0,
    amount_paid: inv.amount_paid ?? 0,
    created: inv.created,
    number: inv.number ?? null,
    hosted_invoice_url: inv.hosted_invoice_url ?? null,
    invoice_pdf: inv.invoice_pdf ?? null,
    customerId: asId(inv.customer),

    donor: compact({
      name: inv.customer_name ?? undefined,
      email: inv.customer_email ?? undefined,
      phone: inv.customer_phone ?? undefined,
      address: inv.customer_address ?? undefined,
    }),

    lines: lines.map((li) =>
      compact({
        id: li.id,
        amount: li.amount,
        currency: li.currency,
        description: li.description ?? undefined,
        quantity: li.quantity ?? undefined,
        price: li.pricing?.price_details?.price ?? null,
        product: li.pricing?.price_details?.product ?? null,
        unit_amount_decimal: li.pricing?.unit_amount_decimal ?? undefined,

        // Normalizamos TODO a string porque LatestInvoiceSlim espera strings
        metadata: li.metadata
          ? {
              donationType:
                li.metadata.donationType != null
                  ? String(li.metadata.donationType)
                  : undefined,
              isFeesCovered:
                li.metadata.isFeesCovered != null
                  ? String(li.metadata.isFeesCovered)
                  : undefined,
              feeDeltaMinor:
                li.metadata.feeDeltaMinor != null
                  ? String(li.metadata.feeDeltaMinor)
                  : undefined,
              productId: li.metadata.productId ?? undefined,
              productFeeId: li.metadata.productFeeId ?? undefined,
              basePriceId: li.metadata.basePriceId ?? undefined,
              currency: li.metadata.currency ?? undefined,
              customerEmail: li.metadata.customerEmail ?? undefined,
              customerName: li.metadata.customerName ?? undefined,
              companyName: li.metadata.companyName ?? undefined,
              displayDonorName:
                li.metadata.displayDonorName != null
                  ? String(li.metadata.displayDonorName)
                  : undefined,
              source: li.metadata.source ?? undefined,
            }
          : undefined,
      })
    ),
  });
}

/** Subscription -> estado (sin object/customer/items/cancel_at_period_end) */
export function mapSubscriptionToStore(
  r: SubscriptionResponse
): StoredSubscription {
  const latest =
    r.latest_invoice && typeof r.latest_invoice === 'object'
      ? slimLatestInvoice(r.latest_invoice as StripeLatestInvoiceExpanded) // <- ESTE
      : null;

  console.log('latestInvoice sended to store', latest);

  return compact<StoredSubscription>({
    id: r.id,
    status: r.status ?? undefined, // p.ej. 'active', 'past_due', etc.
    latest_invoice: latest,
  });
}
